import type { Workbook, Worksheet } from 'exceljs';

import { DateRange } from 'scenes/VisitHistory/utils/range';

import {
  TiendaPerfectaStoreVisitsInsights,
  TiendaPerfectaVisitHistoryStop,
} from 'models/TiendaPerfecta';

import {
  DaySummary,
  MaterialSummary,
  StoreVisitsKpis,
  buildDayRoutes,
  materialKey,
  properCase,
} from './storeVisits';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Cell = string | number | null;

export interface StoreVisitsExportInput {
  stops: TiendaPerfectaVisitHistoryStop[];
  kpis: StoreVisitsKpis;
  days: DaySummary[];
  materials: MaterialSummary;
  insights: TiendaPerfectaStoreVisitsInsights | null;
  range: DateRange;
  zoneNames: string[];
  distributorNames: string[];
  timeZone: string;
  t: Translate;
}

const I18N = 'scenes.store_visits.export';
const PERCENT = '0.0%';
const MAX_WIDTH = 60;

export const storeVisitsFileName = (range: DateRange) =>
  `visitas_locales_${range.from}_${range.to}.xlsx`;

const addSheet = (
  workbook: Workbook,
  name: string,
  headers: string[],
  rows: Cell[][],
  percentColumns: number[] = []
): Worksheet => {
  const sheet = workbook.addWorksheet(name);
  sheet.addRow(headers);
  rows.forEach((row) => sheet.addRow(row));
  sheet.getRow(1).font = { bold: true };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];
  headers.forEach((header, index) => {
    const longest = rows.reduce(
      (max, row) => Math.max(max, String(row[index] ?? '').length),
      header.length
    );
    sheet.getColumn(index + 1).width = Math.min(MAX_WIDTH, longest + 2);
  });
  percentColumns.forEach((col) => {
    for (let r = 2; r <= rows.length + 1; r += 1) {
      sheet.getRow(r).getCell(col + 1).numFmt = PERCENT;
    }
  });
  return sheet;
};

const ratio = (pct: number) => Math.round(pct * 10) / 1000;

/** El libro se arma en el navegador con los mismos datos de la pantalla. */
export const buildStoreVisitsWorkbook = async (
  input: StoreVisitsExportInput
): Promise<Workbook> => {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const { t, kpis, insights } = input;
  const c = (sheet: string, key: string) =>
    t(`${I18N}.columns.${sheet}.${key}`);
  const noReason = t('scenes.store_visits.incidents.no_reason');
  const all = t(`${I18N}.all`);

  addSheet(
    workbook,
    t(`${I18N}.sheets.summary`),
    [c('summary', 'field'), c('summary', 'value')],
    [
      [c('summary', 'from'), input.range.from],
      [c('summary', 'to'), input.range.to],
      [c('summary', 'zones'), input.zoneNames.join(', ') || all],
      [c('summary', 'distributors'), input.distributorNames.join(', ') || all],
      [c('summary', 'planned'), kpis.planned],
      [c('summary', 'executed'), kpis.executed],
      [c('summary', 'incidents'), kpis.incidents],
      [c('summary', 'observations'), insights?.observations.total ?? null],
      [c('summary', 'pending'), kpis.pending],
      [c('summary', 'coverage'), ratio(kpis.coveragePct)],
      [c('summary', 'compliance'), ratio(kpis.compliancePct)],
      [c('summary', 'routes'), kpis.routes],
      [c('summary', 'distributor_count'), kpis.distributors],
      [
        c('summary', 'sells_other'),
        insights ? ratio(insights.competition.sellsOther.pct) : null,
      ],
      [
        c('summary', 'visible_competitor'),
        insights ? ratio(insights.competition.visibleCompetitor.pct) : null,
      ],
    ]
  ).eachRow((row, index) => {
    if ([11, 12, 15, 16].includes(index)) row.getCell(2).numFmt = PERCENT;
  });

  addSheet(
    workbook,
    t(`${I18N}.sheets.days`),
    [
      'date',
      'routes',
      'planned',
      'executed',
      'incidents',
      'pending',
      'compliance',
      'material',
    ].map((k) => c('days', k)),
    [...input.days]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((d) => [
        d.date,
        d.routes,
        d.planned,
        d.executed,
        d.incidents,
        d.pending,
        ratio(d.compliancePct),
        d.materialUnits,
      ]),
    [6]
  );

  const dates = [...new Set(input.stops.map((s) => s.plannedDate))].sort();
  const routes = dates.flatMap((date) =>
    buildDayRoutes(input.stops, date, input.timeZone, noReason)
  );
  addSheet(
    workbook,
    t(`${I18N}.sheets.routes`),
    [
      'date',
      'route',
      'zone',
      'distributor',
      'communes',
      'start',
      'end',
      'planned',
      'executed',
      'incidents',
      'pending',
      'compliance',
    ].map((k) => c('routes', k)),
    routes.map((r) => [
      r.date,
      r.routeCode,
      r.zoneName,
      r.distributorName,
      r.communes.join(', '),
      r.start,
      r.end,
      r.planned,
      r.executed,
      r.incidents,
      r.pending,
      ratio(r.compliancePct),
    ]),
    [11]
  );

  const incidentRows: Cell[][] = input.stops
    .filter((s) => s.outcome === 'incident')
    .map((s) => [
      t(`${I18N}.kind.incident`),
      s.plannedDate,
      s.route.code,
      s.zone.name,
      s.distributor.tradeName,
      s.store.code,
      s.store.label,
      s.incident?.reason || noReason,
      null,
    ]);
  (insights?.observations.items ?? []).forEach((o) =>
    incidentRows.push([
      t(`${I18N}.kind.observation`),
      o.plannedDate,
      o.routeCode,
      o.zoneName,
      o.distributorName,
      o.localCode,
      o.localLabel,
      o.reason,
      o.note,
    ])
  );
  addSheet(
    workbook,
    t(`${I18N}.sheets.incidents`),
    [
      'kind',
      'date',
      'route',
      'zone',
      'distributor',
      'store_code',
      'store',
      'reason',
      'note',
    ].map((k) => c('incidents', k)),
    incidentRows.sort(
      (a, b) =>
        String(a[1]).localeCompare(String(b[1])) ||
        String(a[2]).localeCompare(String(b[2]))
    )
  );

  addSheet(
    workbook,
    t(`${I18N}.sheets.material_totals`),
    ['material', 'units', 'share'].map((k) => c('materials', k)),
    input.materials.all.map((m) => [m.label, m.units, m.pct / 100]),
    [2]
  );

  const byRoute = new Map<
    string,
    { date: string; route: string; label: string; units: number }
  >();
  input.stops.forEach((s) =>
    s.materials.forEach((m) => {
      const key = `${s.plannedDate}|${s.route.code}|${materialKey(
        m.name || m.sku || ''
      )}`;
      const row = byRoute.get(key) ?? {
        date: s.plannedDate,
        route: s.route.code,
        label: properCase(m.name || m.sku || ''),
        units: 0,
      };
      row.units += m.units;
      byRoute.set(key, row);
    })
  );
  addSheet(
    workbook,
    t(`${I18N}.sheets.materials`),
    ['date', 'route', 'material', 'units'].map((k) => c('materials', k)),
    [...byRoute.values()]
      .sort(
        (a, b) =>
          a.date.localeCompare(b.date) ||
          a.route.localeCompare(b.route) ||
          b.units - a.units
      )
      .map((r) => [r.date, r.route, r.label, r.units])
  );

  return workbook;
};
